import { c as _c } from "react/compiler-runtime";
// `<OgShell>` — the brand frame every OG image renders inside.
//
// Carries the 1200×630 frame, raster background, wordmark, and top
// `morfi.markets` attribution.
// Per-surface JSX is the `children` slot. Variants (default / celebration
// / data / mono) tune the chrome for different surface families.
//
// To iterate on shell visuals without redeploying: run `pnpm dev` and
// open `/workspace` — the OG preview panel renders this component live
// (HMR) plus the Satori PNG side-by-side. Edit any variant or chrome
// file under `lib/og/shell/**` and the JSX preview updates instantly.

import type { ReactNode } from 'react';
import type { OgShellVariant } from '../registry';
import { ShellCelebration } from './variants/celebration';
import { ShellData } from './variants/data';
import { ShellDefault } from './variants/default';
import { ShellGift } from './variants/gift';
import { ShellMono } from './variants/mono';
export interface OgShellProps {
  /** Visual treatment. Surface authors typically don't pass this directly —
   *  `renderOg()` reads it from the registry. Override at render time only
   *  for data-driven variants (e.g. resolution showing celebration when a
   *  winner exists, mono when the market is cancelled). */
  variant?: OgShellVariant;
  /** Optional small label rendered above the `morfi.markets` footer
   *  (e.g. `'Gift'`, `'Market'`). Surface-specific. */
  eyebrow?: string | null;
  /** When `true`, renders a `[DEMO MODE]` badge top-right. Set by route
   *  handlers when the request URL carries `?demo-mode` (a devnet share).
   *  Helps recipients on Twitter / Slack / iMessage instantly recognise
   *  pre-prod content without examining the URL. */
  demoMode?: boolean;
  /** Overrides the variant default gap between header chrome and content. */
  contentGap?: number;
  /** Per-surface content. Composed using the primitives in
   *  `lib/og/primitives/`. */
  children: ReactNode;
}
export function OgShell(props) {
  const $ = _c(25);
  const variant = props.variant ?? "default";
  const eyebrow = props.eyebrow ?? null;
  const demoMode = props.demoMode === true;
  switch (variant) {
    case "celebration":
      {
        let t0;
        if ($[0] !== props.contentGap) {
          t0 = props.contentGap !== undefined ? {
            contentGap: props.contentGap
          } : {};
          $[0] = props.contentGap;
          $[1] = t0;
        } else {
          t0 = $[1];
        }
        let t1;
        if ($[2] !== demoMode || $[3] !== eyebrow || $[4] !== props.children || $[5] !== t0) {
          t1 = <ShellCelebration eyebrow={eyebrow} demoMode={demoMode} {...t0}>{props.children}</ShellCelebration>;
          $[2] = demoMode;
          $[3] = eyebrow;
          $[4] = props.children;
          $[5] = t0;
          $[6] = t1;
        } else {
          t1 = $[6];
        }
        return t1;
      }
    case "data":
      {
        let t0;
        if ($[7] !== props.contentGap) {
          t0 = props.contentGap !== undefined ? {
            contentGap: props.contentGap
          } : {};
          $[7] = props.contentGap;
          $[8] = t0;
        } else {
          t0 = $[8];
        }
        let t1;
        if ($[9] !== demoMode || $[10] !== eyebrow || $[11] !== props.children || $[12] !== t0) {
          t1 = <ShellData eyebrow={eyebrow} demoMode={demoMode} {...t0}>{props.children}</ShellData>;
          $[9] = demoMode;
          $[10] = eyebrow;
          $[11] = props.children;
          $[12] = t0;
          $[13] = t1;
        } else {
          t1 = $[13];
        }
        return t1;
      }
    case "mono":
      {
        let t0;
        if ($[14] !== demoMode || $[15] !== eyebrow || $[16] !== props.children) {
          t0 = <ShellMono eyebrow={eyebrow} demoMode={demoMode}>{props.children}</ShellMono>;
          $[14] = demoMode;
          $[15] = eyebrow;
          $[16] = props.children;
          $[17] = t0;
        } else {
          t0 = $[17];
        }
        return t0;
      }
    case "gift":
      {
        let t0;
        if ($[18] !== demoMode || $[19] !== props.children) {
          t0 = <ShellGift demoMode={demoMode}>{props.children}</ShellGift>;
          $[18] = demoMode;
          $[19] = props.children;
          $[20] = t0;
        } else {
          t0 = $[20];
        }
        return t0;
      }
    default:
      {
        let t0;
        if ($[21] !== demoMode || $[22] !== eyebrow || $[23] !== props.children) {
          t0 = <ShellDefault eyebrow={eyebrow} demoMode={demoMode}>{props.children}</ShellDefault>;
          $[21] = demoMode;
          $[22] = eyebrow;
          $[23] = props.children;
          $[24] = t0;
        } else {
          t0 = $[24];
        }
        return t0;
      }
  }
}